/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo } from "react";

import { GridCallbackDetails, GridPaginationModel } from "@mui/x-data-grid-pro";
import { useAppNavigate } from "contexts";
import { dateToISOString } from "helpers/dayjs";
import capitalize from "lodash/capitalize";
import has from "lodash/has";
import queryString from "query-string";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";

import useDebounceState from "./useDebounceState";
import usePushRoute from "./usePushRoute";

const useFilterForm = <T>({
  defaultValues,
  configs,
  watchFormFn,
}: {
  defaultValues: T;
  configs?: {
    pagination?: boolean;
    withUrl?: boolean;
    mode?: "submit" | "onChange";
    dateFields?: (keyof T)[];
    rangeDateFields?: (keyof T)[];
    autocompleteFields?: (keyof T)[];
    multiAutocompleteFields?: (keyof T)[];
    timeOutDefault?: number;
  };
  watchFormFn?: (e: { value: T; name: string; type: "change" }) => void;
}) => {
  const { push } = useAppNavigate();
  const { searchLocation } = usePushRoute();

  const location = useLocation();
  const [filters, setFilters] = useDebounceState<T>(
    defaultValues,
    configs?.timeOutDefault || 0
  );
  const methods = useForm({
    defaultValues: defaultValues as any,
  });
  const {
    pagination = false,
    withUrl = false,
    mode = "onChange",
    dateFields = [],
    rangeDateFields = [],
    autocompleteFields = [],
    multiAutocompleteFields = [],
  } = configs || {};

  useEffect(() => {
    const subscription = methods.watch((_value, { name, type }) => {
      let value = methods.getValues();
      if (watchFormFn) {
        watchFormFn({ value, name: name as never, type: type as never });
      }
      if (pagination || has(value, "page")) {
        if (name !== "page" && value?.page !== 1) {
          methods.setValue("page", 1);
          value = {
            ...value,
            page: 1,
          };
        }
      }

      if (mode === "onChange") {
        setFilters(value as T);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (withUrl) {
      const filterFormat: Record<string, unknown> = {};
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (
          ![
            ...dateFields,
            ...rangeDateFields,
            ...autocompleteFields,
            ...multiAutocompleteFields,
          ]?.includes(key as keyof T)
        ) {
          filterFormat[key] = value;
        }

        if ((dateFields as any)?.includes(key)) {
          filterFormat[key] = value ? dateToISOString(value as string) : null;
        }

        if ((rangeDateFields as any)?.includes(key) && Array.isArray(value)) {
          filterFormat[`from${capitalize(key)}`] = value?.[0]
            ? dateToISOString(value?.[0] as string, "startDay")
            : null;
          filterFormat[`to${capitalize(key)}`] = value?.[1]
            ? dateToISOString(value?.[1] as string, "endDate")
            : null;
        }

        if ((autocompleteFields as any)?.includes(key)) {
          Object.entries(value || {}).forEach(([subKey, subValue]) => {
            if (typeof subValue !== "object") {
              filterFormat[`${key}${capitalize(subKey)}`] = subValue;
            }
          });
        }

        if (
          (multiAutocompleteFields as any)?.includes(key) &&
          Array.isArray(value)
        ) {
          const listLabel: any = [];
          const listValue: any = [];
          value.forEach((item) => {
            listLabel.push(item?.label);
            listValue.push(item?.value);
          });

          filterFormat[`${key}Labels`] = listLabel;
          filterFormat[`${key}Values`] = listValue;
        }
      });

      push({
        to: location.pathname,
        params: queryString.stringify({ ...searchLocation, ...filterFormat }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const filterFormatted = useMemo((): T => {
    const filterFormat: Record<string, unknown> = {};
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (
        ![
          ...dateFields,
          ...rangeDateFields,
          ...autocompleteFields,
          ...multiAutocompleteFields,
        ]?.includes(key as keyof T)
      ) {
        filterFormat[key] = value;
      }

      if ((dateFields as any)?.includes(key)) {
        filterFormat[key] = value ? dateToISOString(value as string) : null;
      }

      if ((rangeDateFields as any)?.includes(key) && Array.isArray(value)) {
        filterFormat[`from${capitalize(key)}`] = value?.[0]
          ? dateToISOString(value?.[0] as string, "startDay")
          : null;
        filterFormat[`to${capitalize(key)}`] = value?.[1]
          ? dateToISOString(value?.[1] as string, "endDate")
          : null;
      }

      if ((autocompleteFields as any)?.includes(key)) {
        filterFormat[key] = (value as any)?.value;
      }

      if (
        (multiAutocompleteFields as any)?.includes(key) &&
        Array.isArray(value)
      ) {
        filterFormat[key] = (value as any)?.map(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          (item: any) => item?.value || undefined
        );
      }
    });
    return filterFormat as T;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const paginationModel = useMemo(() => {
    return {
      page: Number((filters as any)?.page || 1) - 1,
      pageSize: Number((filters as any)?.limit || 10),
    };
  }, [filters]);

  const onPaginationModelChange = useCallback(
    ({ page, pageSize }: GridPaginationModel, event: GridCallbackDetails) => {
      if (event.reason === "setPaginationModel") {
        if (String(pageSize) !== String(methods.getValues("limit"))) {
          methods.setValue("limit", pageSize);
        }
        if (page + 1 !== methods.getValues("page")) {
          methods.setValue("page", page + 1);
        }
      }
    },
    [methods]
  );

  const onSubmit = useCallback(() => {
    setFilters(methods.getValues() as T);
  }, [methods, setFilters]);

  const onReset = useCallback(() => {
    methods.reset(defaultValues);
    if (mode === "submit") {
      setFilters(defaultValues);
    }
  }, [defaultValues, methods, mode, setFilters]);

  return {
    filterFormatted,
    filters,
    filterCustom: filters as any,
    methods,
    paginationModel,
    onPaginationModelChange,
    onSubmit,
    onReset,
  };
};

export default useFilterForm;
